import { Step } from 'react-joyride';
import { joyrideStep, joyrideWelcome } from '../../../../shared/tours/sittJoyrideTheme';
import { CentroTabId } from '../types/centro.types';

export const CENTRO_GENERAL_TOUR_KEY = 'centro-form-general';

export const centroGeneralTourSteps: Step[] = [
    joyrideWelcome(
        'Centros de servicio',
        'Aquí registra o edita una sucursal de taller: datos fiscales, cobertura de servicios, expediente técnico y ubicación en mapa.',
    ),
    joyrideStep(
        '#centro-form-header',
        'Encabezado',
        'Indica si está dando de alta o editando un centro. En edición verá el estatus actual de la sucursal.',
        'bottom',
    ),
    joyrideStep(
        '#centro-form-tour-actions',
        'Guías interactivas',
        'Use «Guía del módulo» para un recorrido general. En cada pestaña, «¿Cómo llenar esta pestaña?» explica campo por campo lo que debe capturar.',
        'bottom',
    ),
    joyrideStep(
        '#centro-form-tabs',
        'Pestañas del formulario',
        'El alta se divide en cuatro pestañas. Complete la información en orden; puede cambiar de pestaña cuando lo necesite.',
        'bottom',
    ),
    joyrideStep(
        '#centro-tab-btn-atencion',
        'General y Fiscales',
        'Proveedor, sucursal, datos bancarios, contactos y horarios de atención.',
        'bottom',
    ),
    joyrideStep(
        '#centro-tab-btn-servicios',
        'Cobertura',
        'Defina qué tipos de servicio y vehículos puede atender esta sucursal.',
        'bottom',
    ),
    joyrideStep(
        '#centro-tab-btn-expediente',
        'Expediente técnico',
        'Personal, infraestructura y equipo disponible en el taller.',
        'bottom',
    ),
    joyrideStep(
        '#centro-tab-btn-location',
        'Ubicación',
        'Dirección, coordenadas y radio de cobertura geográfica.',
        'bottom',
    ),
    joyrideStep(
        '#centro-form-save',
        'Guardar',
        'Al terminar, pulse «Crear Centro» o «Guardar Cambios». Los datos se validan antes de enviarse al servidor.',
        'top',
    ),
];

const tabTourSteps: Record<CentroTabId, Step[]> = {
    atencion: [
        joyrideWelcome(
            'General y Fiscales',
            'Capture la identidad de la sucursal, datos para pagos y quién contactar en operación, administración y facturación.',
        ),
        joyrideStep(
            '#centro-atencion-proveedor',
            'Proveedor',
            'Entidad legal del taller. En alta, seleccione el proveedor; en edición este dato no se modifica.',
        ),
        joyrideStep(
            '#centro-atencion-sucursal',
            'Nombre de sucursal',
            'Nombre comercial con el que identificará esta ubicación (ej. «Monterrey Norte»).',
        ),
        joyrideStep(
            '#centro-atencion-tipo',
            'Categoría de centro',
            'Clasificación del tipo de taller según el catálogo del sistema.',
        ),
        joyrideStep(
            '#centro-atencion-banco',
            'Banco',
            'Institución para depósitos de esta sucursal. Puede heredarse del proveedor y ajustarse si la cuenta es distinta.',
        ),
        joyrideStep(
            '#centro-atencion-clabe',
            'CLABE',
            'Clave interbancaria de 18 dígitos para transferencias a esta sucursal.',
        ),
        joyrideStep(
            '#centro-atencion-caratula',
            'Carátula bancaria',
            'Adjunte el PDF de la carátula de estado de cuenta que respalde la CLABE capturada.',
        ),
        joyrideStep(
            '#centro-atencion-contacto-programacion',
            'Contacto de programación',
            'Asesor que agenda citas: nombre, teléfono y correo. Es el contacto principal para órdenes de trabajo.',
        ),
        joyrideStep(
            '#centro-atencion-contacto-admin',
            'Contacto de administración',
            'Persona para temas operativos internos del taller (opcional pero recomendado).',
        ),
        joyrideStep(
            '#centro-atencion-contacto-facturacion',
            'Contacto de facturación',
            'Persona o área que recibe solicitudes de facturación y complementos.',
        ),
        joyrideStep(
            '#centro-atencion-horarios',
            'Horarios por día',
            'Active cada día laborable y defina hora de apertura y cierre. Cada día puede tener un horario distinto.',
        ),
        joyrideStep(
            '#centro-atencion-slots',
            'Asignación automática',
            'Si activa esta opción, configure bloques horarios y cupos máximos de citas por hora.',
        ),
    ],
    servicios: [
        joyrideWelcome(
            'Cobertura operativa',
            'Indique qué servicios y vehículos puede atender la sucursal. Esto no define precios; solo el alcance operativo.',
        ),
        joyrideStep(
            '#centro-servicios-filtro',
            'Alcance del servicio',
            'Elija categoría (obligatoria). Subcategoría y detalle son opcionales: déjelos vacíos para aplicar «todas» o «todos».',
        ),
        joyrideStep(
            '#centro-servicios-vehiculo',
            'Alcance del vehículo',
            'Marque si la regla aplica a todas las marcas o a una específica. Modelo es opcional.',
        ),
        joyrideStep(
            '#centro-servicios-agregar',
            'Agregar regla',
            'Pulse para añadir la combinación a la lista. Evite duplicar la misma regla.',
        ),
        joyrideStep(
            '#centro-servicios-tabla',
            'Reglas configuradas',
            'Revise las reglas activas. Use el botón de quitar para eliminar una que ya no aplique.',
        ),
    ],
    expediente: [
        joyrideWelcome(
            'Expediente técnico',
            'Documente capacidades reales del taller: personal, instalaciones y equipo especializado.',
        ),
        joyrideStep(
            '#centro-expediente-personal',
            'Personal operativo',
            'Marque los roles con los que cuenta la sucursal (gerente, técnicos, pintor, etc.).',
        ),
        joyrideStep(
            '#centro-expediente-operacion',
            'Operación',
            'Indique si ofrece entrega el mismo día cuando aplique.',
        ),
        joyrideStep(
            '#centro-expediente-infraestructura',
            'Infraestructura',
            'Rampas, sitios productivos, horario de grúa e instalaciones disponibles (estacionamiento, sala de espera, etc.).',
        ),
        joyrideStep(
            '#centro-expediente-equipo',
            'Equipo especializado',
            'Cámara de pintura, banco de enderezado, laboratorio de color y rampa de diagnóstico.',
        ),
    ],
    location: [
        joyrideWelcome(
            'Ubicación',
            'Ubique la sucursal en mapa para asignación de talleres cercanos y validación geográfica.',
        ),
        joyrideStep(
            '#centro-ubicacion-direccion',
            'Dirección',
            'Capture estado, ciudad y domicilio. Son obligatorios para guardar el centro.',
        ),
        joyrideStep(
            '#centro-ubicacion-mapa',
            'Mapa',
            'Busque la dirección, haga clic en el mapa o arrastre el marcador para afinar latitud y longitud.',
        ),
        joyrideStep(
            '#centro-ubicacion-radio',
            'Radio de cobertura',
            'Distancia en metros alrededor del punto donde la sucursal se considera disponible para asignaciones.',
        ),
    ],
};

export function getCentroTabTourSteps(tab: CentroTabId): Step[] {
    return tabTourSteps[tab] ?? [];
}
